import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Linkedin } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 1 0 15.7 15.7V8.29a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const socials = [
  { name: "Facebook", href: "https://web.facebook.com/profile.php?id=100068362992894", Icon: Facebook },
  { name: "LinkedIn", href: "https://www.linkedin.com/showcase/81292112", Icon: Linkedin },
  { name: "TikTok", href: "https://www.tiktok.com/@omnibat_togo", Icon: TikTokIcon },
];

const Footer = () => {
  const { t } = useLanguage();
  const waLink = `https://wa.me/${t.common.whatsappNumber}?text=${encodeURIComponent(t.whatsapp.message)}`;

  return (
    <footer data-testid="site-footer" className="border-t border-line bg-surface">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center">
              <span className="inline-flex rounded-lg bg-white px-3.5 py-2 shadow-sm">
                <img src="/assets/logo-light.png" alt="OMNIBÂT — Bâtir avec exigence, livrer avec confiance" className="h-10 w-auto" />
              </span>
            </div>
            <p className="mt-4 font-display text-lg uppercase tracking-wide text-brand">{t.footer.tagline}</p>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-fog">{t.footer.description}</p>
            <div className="mt-6 flex gap-2">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  data-testid={`social-${s.name.toLowerCase()}-link`}
                  className="flex h-10 w-10 items-center justify-center border border-line text-fog transition-colors hover:border-brand hover:bg-brand hover:text-white"
                >
                  <s.Icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{t.footer.companyTitle}</h3>
            <ul className="mt-5 space-y-3">
              {t.footer.company.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-fog transition-colors hover:text-cream">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{t.footer.servicesTitle}</h3>
            <ul className="mt-5 space-y-3">
              {t.footer.services.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-fog transition-colors hover:text-cream">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{t.footer.contactTitle}</h3>
            <ul className="mt-5 space-y-4 text-sm text-fog">
              <li>
                <a href={`tel:${t.common.phoneRaw}`} className="flex items-center gap-3 hover:text-cream transition-colors">
                  <Phone className="h-4 w-4 text-brand shrink-0" /> {t.common.phone}
                </a>
              </li>
              <li>
                <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-cream transition-colors">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-brand" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  {t.common.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${t.common.email}`} className="flex items-center gap-3 hover:text-cream transition-colors">
                  <Mail className="h-4 w-4 text-brand shrink-0" /> {t.common.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-brand shrink-0 mt-0.5" /> {t.common.address}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-line">
        <div className="mx-auto flex max-w-7xl flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-5">
          <p data-testid="footer-copyright" className="font-mono text-[11px] uppercase tracking-widest text-ash">
            {t.footer.rights}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-widest text-ash">Lomé — Togo · {t.common.email}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
